import { Card, message } from 'antd';
import { DEFAULT_MAXIMUM_FILES, FileUploaded, FormWrapper, UploadInput } from 'components/form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CardTitle } from 'components/claimCard/CardTitle';
import { SelectedClaimTypeObjectType } from '../hooks/useSelectedClaimTypeMapping';
import { breakpoint } from 'styled-components-breakpoint';
import { useFormContext } from 'react-hook-form';
import { ClaimFormState, excludeRemovedDocuments } from '../schema';
import { PreClaimFormState } from '../../preClaim/schema';
import { useCallback, useEffect, useState } from 'react';
import InfoIcon from 'assets/images/icon-info.svg';
import { Typography } from 'antd';
import { MessageType } from '../../../models/MessageType';
const { Text } = Typography;

interface DocumentsCard {
  formStepIndex?: number;
  selectedClaimTypeObject?: SelectedClaimTypeObjectType;
  allowRemoveDocuments?: boolean;
}

const Wrapper = styled.div`
  display: grid;
  row-gap: 26px;
  margin-top: 5px;

  ${breakpoint('desktop')`
    width: 50%;
  `}
`;

export interface ClaimDocuments {
  receipts?: FileUploaded[];
  referralLetters?: FileUploaded[];
  otherDocuments?: FileUploaded[];
  prevClaimSettlements?: FileUploaded[];
}

export const getUploadDocumentParams = (values: ClaimDocuments) => {
  return {
    receiptsLength: excludeRemovedDocuments(values?.receipts).length,
    referralLettersLength: excludeRemovedDocuments(values?.referralLetters).length,
    otherDocumentsLength: excludeRemovedDocuments(values?.otherDocuments).length,
    prevClaimSettlementsLength: excludeRemovedDocuments(values?.prevClaimSettlements).length,
  };
};

export const getNeededRemoveDocuments = (values: ClaimDocuments) => {
  const { receipts, referralLetters, otherDocuments, prevClaimSettlements } = values;
  const isNotUploaded = (uid: string) => {
    const ANT_FILE_UID_PREFIX = 'rc-upload';
    return !uid.startsWith(ANT_FILE_UID_PREFIX);
  };
  const getRemovedDocuments = (documents: FileUploaded[] | undefined) =>
    (documents || [])
      .filter((document: FileUploaded) => document.status === 'removed' && isNotUploaded(document.uid))
      .map((document: FileUploaded) => document.uid);
  return [
    ...getRemovedDocuments(receipts),
    ...getRemovedDocuments(referralLetters),
    ...getRemovedDocuments(otherDocuments),
    ...getRemovedDocuments(prevClaimSettlements),
  ].join(',');
};

const DocumentsCard = ({ formStepIndex = 4, selectedClaimTypeObject, allowRemoveDocuments = false }: DocumentsCard) => {
  const { t } = useTranslation();
  const { trigger, watch, formState, setValue } = useFormContext<ClaimFormState | PreClaimFormState>();
  const [disableUpload, setDisableUpload] = useState(false);
  const receipts = watch('receipts');
  const referralLetters = watch('referralLetters');
  const otherDocuments = watch('otherDocuments');

  const triggerValidationAfterSubmit = useCallback(
    (field: 'receipts' | 'referralLetters') => {
      if (formState.isSubmitted) {
        trigger(field).then();
      }
    },
    [formState.isSubmitted, trigger],
  );

  const handleDisableUploadDocuments = useCallback(() => {
    const receiptsAmount = receipts ? excludeRemovedDocuments(receipts).length : 0;
    const referralLettersAmount = referralLetters ? excludeRemovedDocuments(referralLetters).length : 0;
    const otherDocumentsAmount = otherDocuments ? excludeRemovedDocuments(otherDocuments).length : 0;
    const totalDocuments = receiptsAmount + referralLettersAmount + otherDocumentsAmount;
    let disabled = false;
    if (totalDocuments >= DEFAULT_MAXIMUM_FILES) {
      disabled = true;
    }
    setDisableUpload(disabled);
    if (
      receiptsAmount !== DEFAULT_MAXIMUM_FILES &&
      referralLettersAmount !== DEFAULT_MAXIMUM_FILES &&
      otherDocumentsAmount !== DEFAULT_MAXIMUM_FILES &&
      disabled
    ) {
      message[MessageType.WARNING](t('common.error.noOfFilesExceeds', { amount: DEFAULT_MAXIMUM_FILES }));
    }
  }, [receipts, referralLetters, otherDocuments, t]);

  useEffect(() => {
    handleDisableUploadDocuments();
  }, [handleDisableUploadDocuments]);

  useEffect(() => {
    const hasReferralLetters = (selectedClaimTypeObject?.requiredDocs || []).length > 1;
    if (!hasReferralLetters && referralLetters?.length) {
      setValue('referralLetters', []);
    }
  }, [referralLetters?.length, selectedClaimTypeObject?.requiredDocs, setValue]);

  useEffect(() => {
    triggerValidationAfterSubmit('referralLetters');
  }, [triggerValidationAfterSubmit, receipts]);

  useEffect(() => {
    triggerValidationAfterSubmit('receipts');
  }, [triggerValidationAfterSubmit, referralLetters]);

  return (
    <Card
      id="documents"
      title={<CardTitle formStepIndex={formStepIndex} title={t('newClaim.supplementaryDocuments.title')} />}
    >
      <Text>{t('newClaim.supplementaryDocuments.description')}</Text>
      <Wrapper>
        <FormWrapper
          name="receipts"
          label={selectedClaimTypeObject?.requiredDocs?.[0] || t('newClaim.supplementaryDocuments.receipt')}
          secondaryText={t('newClaim.supplementaryDocuments.uploadingReceiptInfo.description')}
          secondaryTextIcon={<img src={InfoIcon} alt="receiptsIcon" style={{ marginTop: '4px' }} />}
        >
          <UploadInput
            accept="image/*,application/pdf"
            name="receipts"
            allowRemoveDocuments={allowRemoveDocuments}
            disabled={disableUpload}
          />
        </FormWrapper>

        {(selectedClaimTypeObject?.requiredDocs || []).length > 1 && (
          <FormWrapper label={selectedClaimTypeObject?.requiredDocs[1] || ''} name="referralLetters">
            <UploadInput
              accept="image/*,application/pdf"
              name="referralLetters"
              allowRemoveDocuments={allowRemoveDocuments}
              disabled={disableUpload}
            />
          </FormWrapper>
        )}

        <FormWrapper
          name="otherDocuments"
          optionalText={t('common.optional')}
          label={t('newClaim.supplementaryDocuments.otherDocument')}
          secondaryText={t('newClaim.supplementaryDocuments.uploadingOtherDocumentInfo.description')}
          secondaryTextIcon={<img src={InfoIcon} alt="receiptsIcon" style={{ marginTop: '4px' }} />}
        >
          <UploadInput
            accept="image/*,application/pdf"
            name="otherDocuments"
            allowRemoveDocuments={allowRemoveDocuments}
            disabled={disableUpload}
          />
        </FormWrapper>
      </Wrapper>
    </Card>
  );
};

export default DocumentsCard;
